import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { 
  ArrowRight, Upload, CheckCircle2, Clock, Shield, Award, 
  Home, MapPin, Camera, FileText, Phone, Star, Zap
} from 'lucide-react';
import SchemaMarkup from '@/components/SchemaMarkup';
import { buildFAQSchema } from '@/lib/schema';
import OpeningCatalog from '@/components/OpeningCatalog';
import HowItWorks5 from '@/components/HowItWorks5';
import PricingFrameBlock from '@/components/PricingFrameBlock';
import { PrimaryCTA, SecondaryCTA } from '@/components/PrimaryCTA';
import SEO from '@/components/SEO';

export default function LandingPageV2() {
  const navigate = useNavigate();

  const handleCTAClick = (location) => {
    // Track CTA click
    if (window.gtag) {
      window.gtag('event', 'cta_click', {
        location: location,
        event_category: 'engagement',
        event_label: 'Get My Exact Price'
      });
      // Track specific hero CTA
      if (location === 'hero') {
        window.gtag('event', 'cta_clicked_hero', {
          event_category: 'cta_tracking',
          event_label: 'Hero CTA Button'
        });
      }
    }
    if (window.fbq) {
      window.fbq('track', 'ViewContent', { content_name: `${location} CTA Click` });
    }
    navigate('/get-quote');
  };

  // AI-optimized FAQ data
  const pricingFAQ = [
    {
      question: "How much do impact windows cost in Miami?",
      answer: "Most homes in South Florida fall between $15,000 and $28,000 for a full window and door replacement, depending on size, layout, and product selection. Here's a more realistic breakdown based on a typical 12–15 opening home: Good (ECO Windows): ~$14,000 – $16,000, Better (Mr Glass): ~$17,000 – $20,000, Premium (ES Windows): ~$22,000 – $28,000. This includes both windows and doors, which are a major factor in total cost. We provide multiple options so you can choose what fits your budget — no pressure, no in-home sales appointments. Every home is different — your exact price depends on sizes, design pressure requirements, and installation complexity. That's why we built a photo-based quote system.",
      dataTestId: "faq-pricing-miami"
    },
    {
      question: "What affects the cost of impact windows?",
      answer: "Five main factors determine cost: (1) Window size and shape — larger openings and custom shapes (arches, angles) increase cost, (2) Product tier — ECO (Good), Mr Glass (Better), and ES Windows (Premium) each vary in design, performance, and price, (3) Number of openings — larger projects typically reduce the cost per opening, (4) Installation complexity — second-story work, limited access, or structural modifications can increase labor, and (5) Glass type and upgrades — options like Low-E coatings, tint, and higher-performance laminated glass can increase cost depending on the system selected.",
      dataTestId: "faq-cost-factors"
    },
    {
      question: "How much does it cost to replace sliding glass doors with impact doors?",
      answer: "For a standard 72\" x 80\" sliding glass door, here's what most homeowners can expect: Good (ECO): around $2,500 installed, Better (Mr Glass): around $2,800 installed, Premium (ES Windows): around $3,100 installed. Larger openings (8ft–10ft wide) will increase the cost depending on configuration and structural requirements. Entry doors typically range from $2,000 to $4,000 installed, depending on design and material. These are typical prices for standard sizes — final pricing may vary based on layout, access, and installation conditions.",
      dataTestId: "faq-door-cost"
    }
  ];

  const timelineFAQ = [
    {
      question: "How long does impact window installation take?",
      answer: "Most residential impact window installations take 1–3 days, depending on the number of openings. A typical 12-window project takes 2 days. Full-house replacements (20+ windows) may take 3–4 days. Installation scheduling depends on permit approval and product manufacturing lead times, which vary by product tier (Good: ~4 weeks, Better: ~6–8 weeks, Premium: ~6–10 weeks).",
      dataTestId: "faq-install-time"
    },
    {
      question: "How long is the permit process in South Florida?",
      answer: "Permit timelines vary depending on the municipality. In some areas, permits can be approved in as little as 3–5 business days, while others may take 2–4 weeks depending on review times and requirements. Some municipalities offer expedited review windows, while others follow standard review timelines. We handle the entire permitting process, including application, documentation, and coordination with the local building department. Final inspections are scheduled after installation and are typically completed within a few days to a week, depending on the city's availability.",
      dataTestId: "faq-permit-time"
    },
    {
      question: "How long does manufacturing take?",
      answer: "Manufacturing timelines vary depending on the product you select: Good (ECO Windows): ~4 weeks (locally manufactured), Better (Mr. Glass): ~6–8 weeks (locally manufactured), Premium (ES Windows): ~6–10 weeks (may vary due to port and material delays). Custom sizes or specialty shapes may increase these timelines. We only place orders after permits are approved to ensure everything meets current building code requirements. Manufacturing timelines can vary slightly depending on demand and material availability.",
      dataTestId: "faq-manufacturing-time"
    }
  ];

  const permitFAQ = [
    {
      question: "Do I need a permit for impact windows in South Florida?",
      answer: "Yes — all impact window and door installations in South Florida require a building permit in accordance with the Florida Building Code. We handle the entire permitting process for you, including plan preparation, document packaging, and submission to the city. Our permit processing fee is $500, which covers: preparation of required plans, complete permit package assembly, submission to the building department, and HOA coordination and document submission (if applicable). City permit fees are not included and typically range from $150 to $1,000+, depending on the size and scope of the project. Some projects may also require signed and sealed engineering plans (additional cost) or engineering letters for items such as concealed bucks and fasteners (additional cost). If your property has an HOA, approval is often required before we can proceed with permitting or ordering materials. We handle this process by submitting all required documentation, including contractor license and insurance, scope of work, and product approvals and supporting documents. Permit timelines vary depending on the municipality. We coordinate everything so you don't have to deal with the city or your HOA.",
      dataTestId: "faq-permit-required"
    },
    {
      question: "What is required for permit approval?",
      answer: "Permit requirements vary depending on the municipality, but most projects require a combination of the following: completed permit application, product approvals (NOAs) confirming compliance with Florida Building Code / HVHZ, floor plans indicating window and door locations, window and door schedule, HOA approval and/or HOA attestations (if applicable), wind load calculations (required for certain projects), property records (such as warranty deed or property appraiser information), business documentation if the property is owned by an LLC (Sunbiz registration), and payment of city permit fees. We prepare and submit all required documentation and coordinate directly with the building department to move the process forward. Additional documents may be requested by the city depending on the scope of work.",
      dataTestId: "faq-permit-requirements"
    }
  ];

  const processFAQ = [
    {
      question: "How does your process work?",
      answer: (
        <>
          We've designed a streamlined process to make everything as simple and transparent as possible: (1) Upload photos & receive your quote — Submit a few photos through our online form and receive Good, Better, and Premium pricing options (no in-home appointment needed). (2) Review options & sign agreement — Choose the option that fits your needs and sign your contract to move forward. (3) Project setup & documentation — You'll be assigned a dedicated project manager who will guide you through the next steps, including permit and HOA paperwork, and scheduling a field technician to take final measurements and prepare floor plans. (4) Permitting & ordering — We submit your permit application and HOA package (if required), then place your order once approvals are in place. (5) Production & updates — Your windows and doors are manufactured while we keep you updated throughout the process. (6) Installation & inspections — Once materials arrive, we schedule installation, complete the work, and coordinate all required inspections. (7) Project completion — Final details are completed, inspections are passed, and your project is officially complete. From start to finish, we handle every step so you don't have to.{' '}
          <button onClick={() => navigate('/installation-process')} className="text-[var(--brand-ocean-700)] hover:text-[var(--brand-ocean-900)] font-semibold underline">View complete installation timeline →</button>
        </>
      ),
      dataTestId: "faq-process"
    },
    {
      question: "Can I get a quote without an in-home estimate?",
      answer: "Yes. Our remote quoting system works by analyzing photos you upload. This eliminates the need for 2–3 hour in-home sales presentations. Most customers receive accurate quotes within 24 hours. In-person visits are optional and only scheduled if you request one after reviewing your quote.",
      dataTestId: "faq-remote-quote"
    }
  ];

  const insuranceFAQ = [
    {
      question: "Do impact windows reduce homeowners insurance in Florida?",
      answer: (
        <>
          Yes. Most Florida insurers offer premium discounts ranging from 10% to 45% for homes with certified impact windows. Average savings are 20–30% annually. We provide all documentation needed (permits, NOAs, installation certificates) for you to claim these discounts with your insurance company.{' '}
          <button onClick={() => navigate('/insurance-savings')} className="text-[var(--brand-ocean-700)] hover:text-[var(--brand-ocean-900)] font-semibold underline">Learn about wind mitigation and insurance savings →</button>
        </>
      ),
      dataTestId: "faq-insurance-savings"
    },
    {
      question: "What is a wind mitigation inspection?",
      answer: "A wind mitigation inspection is a report that documents your home's hurricane protection features, including impact windows. Performed by licensed inspectors, it verifies compliance and qualifies you for insurance discounts. Inspections cost $75–$150 and are typically done after installation and final inspection approval.",
      dataTestId: "faq-wind-mitigation"
    }
  ];

  const realProjects = [
    {
      image: 'https://customer-assets.emergentagent.com/job_impact-quote-hub/artifacts/b9vwpak9_WhatsApp%20Image%202026-04-19%20at%204.43.58%20PM.jpeg',
      location: 'Waterfront Condo – North Miami',
      caption: 'Marina view windows with Low-E glass'
    },
    {
      image: 'https://customer-assets.emergentagent.com/job_impact-quote-hub/artifacts/49fwol8g_WhatsApp%20Image%202026-04-19%20at%204.43.58%20PM%20%282%29.jpeg',
      location: 'Sliding Doors – North Miami',
      caption: 'Real installation in progress'
    },
    {
      image: 'https://customer-assets.emergentagent.com/job_impact-quote-hub/artifacts/7p8ln953_WhatsApp%20Image%202026-04-19%20at%204.45.39%20PM%20%281%29.jpeg',
      location: 'Mid-Rise Building – Waterfront',
      caption: 'Multi-unit impact window project'
    },
    {
      image: 'https://customer-assets.emergentagent.com/job_impact-quote-hub/artifacts/p1dw6cbv_WhatsApp%20Image%202026-04-17%20at%2012.19.27%20PM.jpeg',
      location: 'Full House – Kendall',
      caption: '15 windows + 3 doors installed'
    }
  ];

  const testimonials = [
    {
      text: "The whole process was so simple. Uploaded photos, got our quote in 24 hours, and moved forward without any pressure. Saved 25% on insurance.",
      author: "Maria R.",
      location: "Kendall",
      rating: 5
    },
    {
      text: "I was skeptical about quoting without an in-person visit, but the pricing was accurate and installation was flawless. Would absolutely recommend.",
      author: "James T.",
      location: "Coral Gables",
      rating: 5
    },
    {
      text: "Best part was no sales pitch. Just clear options and transparent pricing. Made the decision easy.",
      author: "Linda M.",
      location: "Aventura",
      rating: 5
    }
  ];

  // Merge all string-only FAQ blocks (skip ones with JSX answers — they
  // can't serialize cleanly into FAQPage schema). The order here mirrors
  // the visible order on the page so Google's accuracy heuristic passes.
  const faqSchema = buildFAQSchema([
    ...pricingFAQ,
    ...timelineFAQ,
    ...permitFAQ,
  ]);

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <SEO
        title="Impact Windows & Doors South Florida — Photo-Based Pricing"
        description="Upload photos of your windows and doors, get real Good/Better/Premium installed pricing within 24 hours. No in-home sales appointment required."
        path="/"
      />
      <SchemaMarkup schemas={faqSchema} instanceId="homepage-faq" />
      {/* ========== HERO SECTION (Phase 8: editorial, warm ivory, right-column image on desktop) ========== */}
      <section
        className="relative overflow-hidden bg-gradient-to-b from-[var(--snap-ivory)] via-[var(--snap-ivory)] to-white"
        data-testid="homepage-hero"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 lg:gap-12 items-center">
            {/* Editorial copy column */}
            <div className="lg:col-span-7 text-center lg:text-left">
              <p
                className="text-[11px] sm:text-xs font-semibold tracking-[0.22em] uppercase text-[var(--snap-champagne-600)] mb-5"
                data-testid="hero-eyebrow"
              >
                South Florida Impact Windows &amp; Doors
              </p>
              <h1
                className="font-heading text-[40px] sm:text-[56px] md:text-[68px] lg:text-[76px] leading-[1.02] text-[var(--snap-charcoal)] mb-6 sm:mb-7"
                data-testid="hero-headline"
              >
                Upload Your Openings.<br />
                <span className="text-[var(--snap-navy-900)]">Get Installed Impact Window Pricing.</span>
              </h1>
              <p
                className="text-base sm:text-lg md:text-xl text-[var(--snap-slate-500)] leading-relaxed max-w-xl mx-auto lg:mx-0 mb-8 sm:mb-10"
                data-testid="hero-subheadline"
              >
                Photo-based quotes with Good / Better / Premium installed pricing &mdash; no in-home sales appointment.
              </p>

              <div className="flex flex-col sm:flex-row items-center lg:justify-start justify-center gap-3 sm:gap-4 mb-6">
                <PrimaryCTA
                  src="homepage_hero"
                  label="Get My Price"
                  shortLabel="Get My Price"
                  icon={Upload}
                  testId="hero-cta-get-my-price"
                  fullWidthMobile={false}
                />
                <SecondaryCTA src="homepage_hero" testId="hero-cta-see-how-it-works" />
              </div>

              <p
                className="text-[13px] sm:text-sm italic text-[var(--snap-slate-500)] leading-relaxed max-w-xl mx-auto lg:mx-0"
                data-testid="hero-reassurance"
              >
                No in-home appointment &middot; Final measurements confirmed before ordering &middot; Permits &amp; install included
              </p>

              <div
                className="mt-8 inline-flex items-center gap-2 text-[13px] text-[var(--snap-slate-500)]"
                data-testid="hero-trust-signal"
              >
                <span>Used by homeowners across Miami-Dade, Broward, and Palm Beach.</span>
              </div>
            </div>

            {/* Right-column real-install photo \u2014 desktop only.
                Reuses an existing case study asset. No new asset cost. */}
            <div className="hidden lg:block lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-2 rounded-[28px] bg-[var(--snap-stone-100)]/60 -z-10" aria-hidden="true" />
                <img
                  src="https://customer-assets.emergentagent.com/job_impact-quote-hub/artifacts/p1dw6cbv_WhatsApp%20Image%202026-04-17%20at%2012.19.27%20PM.jpeg"
                  alt="South Florida home with newly installed impact windows"
                  className="w-full aspect-[4/5] object-cover rounded-[24px] border border-[var(--snap-stone-200)]"
                  loading="eager"
                  decoding="async"
                  data-testid="hero-image"
                />
                {/* Quiet caption chip */}
                <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-sm border border-[var(--snap-stone-200)] rounded-lg px-4 py-3">
                  <p className="text-[10.5px] uppercase tracking-[0.22em] text-[var(--snap-champagne-600)] font-semibold mb-1">
                    Recent project
                  </p>
                  <p className="text-[13.5px] text-[var(--snap-charcoal)] leading-snug">
                    Kendall full-home impact window install
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== TRUST LAYER ========== */}
      <section className="py-8 sm:py-12 bg-gradient-to-r from-[var(--brand-ocean-50)]/30 via-white to-[var(--brand-seafoam-50)]/20 border-y border-[var(--brand-ocean-100)]" data-testid="homepage-trust-layer">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            <div className="text-center" data-testid="trust-licensed">
              <Shield className="h-8 w-8 sm:h-10 sm:w-10 text-[var(--brand-ocean-700)] mx-auto mb-2" />
              <p className="text-xs sm:text-sm font-semibold text-[var(--brand-slate-900)]">Fully Licensed & Insured</p>
              <p className="text-[10px] sm:text-xs text-[var(--brand-slate-600)] font-mono mt-1">CGC1524851</p>
            </div>
            <div className="text-center" data-testid="trust-service-area">
              <MapPin className="h-8 w-8 sm:h-10 sm:w-10 text-[var(--brand-ocean-700)] mx-auto mb-2" />
              <p className="text-xs sm:text-sm font-semibold text-[var(--brand-slate-900)]">Serving South Florida</p>
              <p className="text-[10px] sm:text-xs text-[var(--brand-slate-600)]">Miami-Dade, Broward, Palm Beach</p>
            </div>
            <div className="text-center" data-testid="trust-experience">
              <Award className="h-8 w-8 sm:h-10 sm:w-10 text-[var(--brand-ocean-700)] mx-auto mb-2" />
              <p className="text-xs sm:text-sm font-semibold text-[var(--brand-slate-900)]">30+ Years</p>
              <p className="text-[10px] sm:text-xs text-[var(--brand-slate-600)]">Combined Experience</p>
            </div>
            <div className="text-center" data-testid="trust-compliance">
              <CheckCircle2 className="h-8 w-8 sm:h-10 sm:w-10 text-[var(--brand-ocean-700)] mx-auto mb-2" />
              <p className="text-xs sm:text-sm font-semibold text-[var(--brand-slate-900)]">Miami-Dade Approved</p>
              <p className="text-[10px] sm:text-xs text-[var(--brand-slate-600)]">Code-Compliant Products</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== HOW IT WORKS (5 STEPS) ========== */}
      <HowItWorks5 src="homepage_how_it_works" />

      {/* ========== OPENING CATALOG ========== */}
      <OpeningCatalog src="homepage_catalog" />

      {/* ========== PRICING FRAME BLOCK ========== */}
      <PricingFrameBlock src="homepage_pricing_frame" />

      {/* ========== REAL PROJECT EXAMPLES ========== */}
      <section className="py-16 bg-white" data-testid="homepage-case-study-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="font-heading text-3xl font-bold text-[var(--brand-slate-900)] text-center mb-3">
            Real Homeowners, Real Choices
          </h2>
          <p className="text-center text-[var(--brand-slate-700)] mb-10 max-w-3xl mx-auto">
            See how real homeowners chose the right option based on their home, budget, and building requirements.
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* Kendall Case Study */}
            <Card className="rounded-2xl shadow-lg border-2 border-[var(--brand-ocean-100)] overflow-hidden hover:shadow-xl transition-shadow">
              <CardContent className="p-0">
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_impact-quote-hub/artifacts/p1dw6cbv_WhatsApp%20Image%202026-04-17%20at%2012.19.27%20PM.jpeg" 
                    alt="Kendall Full House - Complete Installation"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="font-heading text-xl font-bold text-white mb-1">Kendall Full House</h3>
                    <p className="text-sm text-white/90">Good / Better / Premium Comparison</p>
                  </div>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-3 gap-3 mb-4 pb-4 border-b border-[var(--brand-slate-200)]">
                    <div className="text-center">
                      <p className="text-xs text-[var(--brand-slate-600)]">Cost</p>
                      <p className="text-sm font-bold text-[var(--brand-slate-900)]">$24.5k</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-[var(--brand-slate-600)]">Timeline</p>
                      <p className="text-sm font-bold text-[var(--brand-slate-900)]">8 weeks</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-[var(--brand-slate-600)]">Savings</p>
                      <p className="text-sm font-bold text-[var(--brand-slate-900)]">28%</p>
                    </div>
                  </div>
                  <p className="text-sm text-[var(--brand-slate-700)] mb-4">
                    How a homeowner compared tiers, understood transparent pricing, and chose ES Windows (Premium) without a sales appointment.
                  </p>
                  <Button
                    onClick={() => navigate('/case-study-kendall-full-house')}
                    variant="outline"
                    className="w-full rounded-xl h-11 border-2 border-[var(--brand-ocean-600)] text-[var(--brand-ocean-700)] font-semibold hover:bg-[var(--brand-ocean-50)]"
                  >
                    View Case Study <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* North Miami Case Study */}
            <Card className="rounded-2xl shadow-lg border-2 border-[var(--brand-ocean-100)] overflow-hidden hover:shadow-xl transition-shadow">
              <CardContent className="p-0">
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_impact-quote-hub/artifacts/b9vwpak9_WhatsApp%20Image%202026-04-19%20at%204.43.58%20PM.jpeg" 
                    alt="North Miami Waterfront Condo - Marina View"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="font-heading text-xl font-bold text-white mb-1">North Miami Condo</h3>
                    <p className="text-sm text-white/90">Waterfront with Building Requirements</p>
                  </div>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-3 gap-3 mb-4 pb-4 border-b border-[var(--brand-slate-200)]">
                    <div className="text-center">
                      <p className="text-xs text-[var(--brand-slate-600)]">Cost</p>
                      <p className="text-sm font-bold text-[var(--brand-slate-900)]">$25k</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-[var(--brand-slate-600)]">Timeline</p>
                      <p className="text-sm font-bold text-[var(--brand-slate-900)]">7 weeks</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-[var(--brand-slate-600)]">Type</p>
                      <p className="text-sm font-bold text-[var(--brand-slate-900)]">Condo</p>
                    </div>
                  </div>
                  <p className="text-sm text-[var(--brand-slate-700)] mb-4">
                    How we navigated HOA approval, coastal requirements, and building restrictions for a compliant waterfront installation.
                  </p>
                  <Button
                    onClick={() => navigate('/case-study-north-miami-condo')}
                    variant="outline"
                    className="w-full rounded-xl h-11 border-2 border-[var(--brand-ocean-600)] text-[var(--brand-ocean-700)] font-semibold hover:bg-[var(--brand-ocean-50)]"
                  >
                    View Case Study <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            <Button
              onClick={() => navigate('/case-studies')}
              variant="default"
              className="bg-[var(--brand-ocean-700)] hover:bg-[var(--brand-ocean-800)] text-white font-semibold rounded-xl h-12 px-8"
            >
              View All Case Studies
            </Button>
          </div>
        </div>
      </section>

      {/* ========== VALUE PROPOSITION ========== */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[var(--brand-slate-900)] mb-3">
              Why Choose Our Remote Quoting System
            </h2>
            <p className="text-[var(--brand-slate-600)] max-w-2xl mx-auto">
              Skip the 2–3 hour in-home sales presentation. Get transparent pricing faster.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="rounded-2xl shadow-sm border-2 border-[var(--brand-ocean-100)]">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 rounded-full bg-[var(--brand-ocean-100)] flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-7 w-7 text-[var(--brand-ocean-700)]" />
                </div>
                <h3 className="font-heading font-semibold text-[var(--brand-slate-900)] mb-2">Fast Turnaround</h3>
                <p className="text-sm text-[var(--brand-slate-600)] leading-relaxed">
                  Receive Good/Better/Premium pricing options within 24 hours of photo upload
                </p>
              </CardContent>
            </Card>

            <Card className="rounded-2xl shadow-sm border-2 border-[var(--brand-ocean-100)]">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 rounded-full bg-[var(--brand-ocean-100)] flex items-center justify-center mx-auto mb-4">
                  <Camera className="h-7 w-7 text-[var(--brand-ocean-700)]" />
                </div>
                <h3 className="font-heading font-semibold text-[var(--brand-slate-900)] mb-2">No Appointments</h3>
                <p className="text-sm text-[var(--brand-slate-600)] leading-relaxed">
                  Upload photos online. No need to schedule or wait for in-home visits
                </p>
              </CardContent>
            </Card>

            <Card className="rounded-2xl shadow-sm border-2 border-[var(--brand-ocean-100)]">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 rounded-full bg-[var(--brand-ocean-100)] flex items-center justify-center mx-auto mb-4">
                  <FileText className="h-7 w-7 text-[var(--brand-ocean-700)]" />
                </div>
                <h3 className="font-heading font-semibold text-[var(--brand-slate-900)] mb-2">Transparent Pricing</h3>
                <p className="text-sm text-[var(--brand-slate-600)] leading-relaxed">
                  Compare 3 options side-by-side with clear pricing and product details
                </p>
              </CardContent>
            </Card>

            <Card className="rounded-2xl shadow-sm border-2 border-[var(--brand-ocean-100)]">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 rounded-full bg-[var(--brand-ocean-100)] flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-7 w-7 text-[var(--brand-ocean-700)]" />
                </div>
                <h3 className="font-heading font-semibold text-[var(--brand-slate-900)] mb-2">No Pressure</h3>
                <p className="text-sm text-[var(--brand-slate-600)] leading-relaxed">
                  Review quotes on your time. Decide when you're ready. No pushy sales tactics
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ========== PRODUCT TIERS (GOOD/BETTER/PREMIUM) ========== */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-[var(--brand-slate-900)] mb-4">
              Choose the Right Option for Your Home
            </h2>
            <p className="text-base text-[var(--brand-slate-700)] max-w-2xl mx-auto mb-2">
              Compare your options side-by-side and choose what works best for your home and budget.
            </p>
            <p className="text-sm text-[var(--brand-slate-600)]">
              Fully permitted and code-compliant installations • Condo and HOA experience
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* GOOD Tier */}
            <Card className="rounded-2xl shadow-md border-2 border-[var(--brand-ocean-100)] overflow-hidden">
              <CardContent className="p-6">
                <div className="text-center mb-4">
                  <div className="inline-block px-4 py-1.5 bg-[var(--brand-ocean-100)] rounded-full mb-4">
                    <span className="text-sm font-semibold text-[var(--brand-ocean-700)]">GOOD</span>
                  </div>
                </div>
                <div className="flex items-center justify-center mb-4 h-24">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_impact-quote-hub/artifacts/w9jekft2_eco_window_systems_logo%20%281%29.jpg" 
                    alt="ECO Window Systems"
                    className="max-h-20 w-auto object-contain"
                  />
                </div>
                <p className="text-sm text-[var(--brand-slate-600)] text-center">
                  Quality protection at an accessible price point
                </p>
              </CardContent>
            </Card>

            {/* BETTER Tier */}
            <Card className="rounded-2xl shadow-lg border-2 border-[var(--brand-ocean-600)] overflow-hidden">
              <CardContent className="p-6">
                <div className="text-center mb-4">
                  <div className="inline-block px-4 py-1.5 bg-[var(--brand-ocean-600)] rounded-full mb-4">
                    <span className="text-sm font-semibold text-white">BETTER</span>
                  </div>
                </div>
                <div className="flex items-center justify-center mb-4 h-24">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_impact-quote-hub/artifacts/1edhksrg_Mr-Glass-Logo.png" 
                    alt="Mr Glass"
                    className="max-h-20 w-auto object-contain"
                  />
                </div>
                <p className="text-sm text-[var(--brand-slate-600)] text-center">
                  Enhanced performance and durability
                </p>
              </CardContent>
            </Card>

            {/* PREMIUM Tier - MOST PROMINENT */}
            <Card className="rounded-2xl shadow-xl border-[3px] border-[var(--brand-ocean-700)] overflow-hidden bg-gradient-to-b from-white to-[var(--brand-ocean-50)]/20">
              <CardContent className="p-6">
                <div className="text-center mb-4">
                  <div className="inline-block px-5 py-2 bg-gradient-to-r from-blue-900 to-blue-950 rounded-full mb-4 shadow-md">
                    <span className="text-sm font-bold text-white tracking-wide" style={{color: '#ffffff'}}>PREMIUM</span>
                  </div>
                </div>
                <div className="flex items-center justify-center mb-4 h-24 bg-white rounded-xl p-3">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_impact-quote-hub/artifacts/dt288m9t_ESWindow-Logo_Web.png" 
                    alt="ES Windows"
                    className="max-h-20 w-auto object-contain"
                  />
                </div>
                <p className="text-sm text-[var(--brand-slate-700)] text-center font-medium">
                  Top-tier protection and energy efficiency
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <Button
              onClick={() => handleCTAClick('product-tiers')}
              size="lg"
              data-testid="product-tiers-cta"
              className="bg-[var(--brand-ocean-700)] hover:bg-[var(--brand-ocean-800)] text-white rounded-xl h-14 px-8 text-base font-semibold shadow-lg"
            >
              <Upload className="mr-2 h-4 w-4" />
              Get My Price
            </Button>
            <p className="text-xs text-[var(--brand-slate-600)] mt-3">
              No in-home appointment required • No pressure • Fast turnaround
            </p>
          </div>
        </div>
      </section>

      {/* ========== BEFORE & AFTER VIDEO (KENDALL PROJECT) ========== */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-10">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[var(--brand-slate-900)] mb-3 sm:mb-4">
              See a Real Project Transformation
            </h2>
            <p className="text-sm sm:text-base text-[var(--brand-slate-600)] max-w-2xl mx-auto mb-6 sm:mb-8 px-2">
              From placing the order to installation, this Kendall project took about 8 weeks due to manufacturing lead time. Installation was completed in 2 days, and the homeowner later reduced insurance costs by 28%.
            </p>
          </div>

          <div className="rounded-xl sm:rounded-2xl overflow-hidden shadow-lg sm:shadow-xl border border-[var(--brand-ocean-100)] sm:border-2">
            <div className="relative bg-black aspect-video">
              <video 
                controls 
                playsInline
                preload="metadata"
                className="w-full h-full object-contain"
                data-testid="kendall-project-video"
              >
                <source src="https://customer-assets.emergentagent.com/job_impact-quote-hub/artifacts/1gpqqfsg_WhatsApp%20Video%202026-03-03%20at%209.22.47%20AM.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 sm:p-4 pointer-events-none">
                <p className="text-white font-semibold text-xs sm:text-sm">Full home impact window replacement in Kendall</p>
              </div>
            </div>
          </div>

          <div className="text-center mt-4 sm:mt-6">
            <Button
              onClick={() => navigate('/case-study-kendall-full-house')}
              variant="outline"
              className="rounded-xl h-11 sm:h-12 border-2 border-[var(--brand-ocean-600)] text-[var(--brand-ocean-700)] font-semibold hover:bg-[var(--brand-ocean-50)] w-full sm:w-auto px-6"
              data-testid="video-view-full-project"
            >
              View Full Project <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* ========== TEAM IN ACTION VIDEO ========== */}
      <section className="py-12 sm:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-10">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[var(--brand-slate-900)] mb-3 sm:mb-4">
              See Our Team in Action
            </h2>
            <p className="text-sm sm:text-base text-[var(--brand-slate-600)] max-w-2xl mx-auto px-2">
              Professional installation by experienced crews — every project handled with care.
            </p>
          </div>

          <div className="rounded-xl sm:rounded-2xl overflow-hidden shadow-lg sm:shadow-xl border border-[var(--brand-ocean-100)] sm:border-2">
            <div className="relative bg-black aspect-video">
              <video 
                controls 
                playsInline
                preload="metadata"
                className="w-full h-full object-contain"
                data-testid="team-in-action-video"
              >
                <source src="https://customer-assets.emergentagent.com/job_impact-quote-hub/artifacts/vj07w1rj_WhatsApp%20Video%202026-03-02%20at%204.57.13%20PM.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* ========== HOW IT WORKS ========== */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-[var(--brand-ocean-50)]/30 via-white to-[var(--brand-seafoam-50)]/20" data-testid="homepage-how-it-works">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[var(--brand-slate-900)] mb-4">
              How It Works
            </h2>
            <p className="text-base sm:text-lg text-[var(--brand-slate-600)] max-w-2xl mx-auto">
              Get a real impact window quote in minutes — without a sales appointment
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 mb-12">
            <div className="text-center" data-testid="step-upload-photos">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-[var(--brand-ocean-100)] flex items-center justify-center mx-auto mb-4">
                <Camera className="h-8 w-8 sm:h-10 sm:w-10 text-[var(--brand-ocean-700)]" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-[var(--brand-slate-900)] mb-2">
                1. Upload Photos
              </h3>
              <p className="text-sm text-[var(--brand-slate-600)]">
                Upload photos of your home through our guided flow
              </p>
            </div>

            <div className="text-center" data-testid="step-review">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-[var(--brand-ocean-100)] flex items-center justify-center mx-auto mb-4">
                <FileText className="h-8 w-8 sm:h-10 sm:w-10 text-[var(--brand-ocean-700)]" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-[var(--brand-slate-900)] mb-2">
                2. We Review & Build
              </h3>
              <p className="text-sm text-[var(--brand-slate-600)]">
                We review and build your quote with Good / Better / Premium options
              </p>
            </div>

            <div className="text-center" data-testid="step-receive">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-[var(--brand-ocean-100)] flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-8 w-8 sm:h-10 sm:w-10 text-[var(--brand-ocean-700)]" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-[var(--brand-slate-900)] mb-2">
                3. Receive Quote
              </h3>
              <p className="text-sm text-[var(--brand-slate-600)]">
                You receive your quote within 24–48 hours
              </p>
            </div>

            <div className="text-center" data-testid="step-move-forward">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-[var(--brand-ocean-100)] flex items-center justify-center mx-auto mb-4">
                <Home className="h-8 w-8 sm:h-10 sm:w-10 text-[var(--brand-ocean-700)]" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-[var(--brand-slate-900)] mb-2">
                4. Move Forward
              </h3>
              <p className="text-sm text-[var(--brand-slate-600)]">
                We handle measurements and permits if you choose to proceed
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-md border-2 border-[var(--brand-ocean-100)]">
            <div className="grid md:grid-cols-3 gap-4 sm:gap-6">
              <div className="flex items-start gap-3" data-testid="benefit-no-sales">
                <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-[var(--brand-slate-900)] text-sm sm:text-base">No in-home sales pitch</p>
                  <p className="text-xs sm:text-sm text-[var(--brand-slate-600)]">Skip the 2-hour presentation</p>
                </div>
              </div>
              <div className="flex items-start gap-3" data-testid="benefit-transparent">
                <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-[var(--brand-slate-900)] text-sm sm:text-base">Transparent pricing</p>
                  <p className="text-xs sm:text-sm text-[var(--brand-slate-600)]">Clear breakdown, no games</p>
                </div>
              </div>
              <div className="flex items-start gap-3" data-testid="benefit-fast">
                <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-[var(--brand-slate-900)] text-sm sm:text-base">Fast turnaround</p>
                  <p className="text-xs sm:text-sm text-[var(--brand-slate-600)]">Most quotes within 24–48 hours</p>
                </div>
              </div>
            </div>
          </div>

          {/* Backup CTA */}
          <div className="mt-8 text-center" data-testid="backup-cta-section">
            <p className="text-sm text-[var(--brand-slate-600)] mb-3">
              Not ready to upload photos?
            </p>
            <a 
              href="tel:855-784-7627"
              className="inline-flex items-center gap-2 text-[var(--brand-ocean-700)] hover:text-[var(--brand-ocean-900)] font-semibold text-sm sm:text-base transition-colors"
              data-testid="backup-cta-call"
            >
              <Phone className="h-4 w-4" />
              Schedule a quick call — 855-784-SNAP
            </a>
          </div>
        </div>
      </section>

      {/* ========== SOCIAL PROOF / TESTIMONIALS ========== */}
      <section className="py-16 bg-white" data-testid="homepage-testimonials">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="font-heading text-3xl font-bold text-[var(--brand-slate-900)] text-center mb-10">
            What Homeowners Say
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.author} className="rounded-2xl shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-3">
                    {[...Array(testimonial.rating)].map((_, j) => (
                      <Star key={j} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-sm text-[var(--brand-slate-700)] mb-4 leading-relaxed italic">
                    "{testimonial.text}"
                  </p>
                  <div className="border-t pt-3">
                    <p className="font-semibold text-sm text-[var(--brand-slate-900)]">{testimonial.author}</p>
                    <p className="text-xs text-[var(--brand-slate-600)]">{testimonial.location}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ========== PRICING FAQ (AI-OPTIMIZED) ========== */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="font-heading text-3xl font-bold text-[var(--brand-slate-900)] mb-8">
            Impact Window Pricing
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            {pricingFAQ.map((faq, i) => (
              <AccordionItem key={faq.question} value={`pricing-${i}`} className="bg-[var(--brand-ocean-50)] rounded-xl border-2 border-[var(--brand-ocean-100)] px-5" data-testid={faq.dataTestId}>
                <AccordionTrigger className="font-heading font-semibold text-base text-left text-[var(--brand-slate-900)] hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-[var(--brand-slate-600)] pb-5 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ========== TIMELINE FAQ (AI-OPTIMIZED) ========== */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="font-heading text-3xl font-bold text-[var(--brand-slate-900)] mb-8">
            Timeline & Installation
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            {timelineFAQ.map((faq, i) => (
              <AccordionItem key={faq.question} value={`timeline-${i}`} className="bg-white rounded-xl border-2 border-[var(--brand-ocean-100)] px-5" data-testid={faq.dataTestId}>
                <AccordionTrigger className="font-heading font-semibold text-base text-left text-[var(--brand-slate-900)] hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-[var(--brand-slate-600)] pb-5 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ========== PERMIT FAQ (AI-OPTIMIZED) ========== */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="font-heading text-3xl font-bold text-[var(--brand-slate-900)] mb-8">
            Permits & Code Compliance
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            {permitFAQ.map((faq, i) => (
              <AccordionItem key={faq.question} value={`permit-${i}`} className="bg-[var(--brand-ocean-50)] rounded-xl border-2 border-[var(--brand-ocean-100)] px-5" data-testid={faq.dataTestId}>
                <AccordionTrigger className="font-heading font-semibold text-base text-left text-[var(--brand-slate-900)] hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-[var(--brand-slate-600)] pb-5 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ========== PROCESS FAQ (AI-OPTIMIZED) ========== */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="font-heading text-3xl font-bold text-[var(--brand-slate-900)] mb-8">
            Our Process
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            {processFAQ.map((faq, i) => (
              <AccordionItem key={faq.question} value={`process-${i}`} className="bg-white rounded-xl border-2 border-[var(--brand-ocean-100)] px-5" data-testid={faq.dataTestId}>
                <AccordionTrigger className="font-heading font-semibold text-base text-left text-[var(--brand-slate-900)] hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-[var(--brand-slate-600)] pb-5 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ========== INSURANCE FAQ (AI-OPTIMIZED) ========== */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="font-heading text-3xl font-bold text-[var(--brand-slate-900)] mb-8">
            Insurance & Savings
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            {insuranceFAQ.map((faq, i) => (
              <AccordionItem key={faq.question} value={`insurance-${i}`} className="bg-[var(--brand-ocean-50)] rounded-xl border-2 border-[var(--brand-ocean-100)] px-5" data-testid={faq.dataTestId}>
                <AccordionTrigger className="font-heading font-semibold text-base text-left text-[var(--brand-slate-900)] hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-[var(--brand-slate-600)] pb-5 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ========== REAL PROJECTS ========== */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="font-heading text-3xl font-bold text-[var(--brand-slate-900)] text-center mb-12">
            Real Homes We've Protected
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {realProjects.map((project) => (
              <div key={project.location} className="group relative overflow-hidden rounded-2xl shadow-md hover:shadow-xl transition-all duration-300">
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src={project.image} 
                    alt={project.location}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex flex-col justify-end p-4">
                  <p className="text-white font-semibold text-sm mb-1">{project.location}</p>
                  <p className="text-white/80 text-xs">{project.caption}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <p className="text-sm text-[var(--brand-slate-600)] mb-4">
              Real installation photos from recent projects. See complete project breakdowns:
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => navigate('/case-study-kendall-full-house')}
                variant="outline"
                className="rounded-xl h-11 border-2 border-[var(--brand-ocean-600)] text-[var(--brand-ocean-700)] font-semibold hover:bg-[var(--brand-ocean-50)]"
              >
                Kendall Full House ($24.5k)
              </Button>
              <Button
                onClick={() => navigate('/case-study-north-miami-condo')}
                variant="outline"
                className="rounded-xl h-11 border-2 border-[var(--brand-ocean-600)] text-[var(--brand-ocean-700)] font-semibold hover:bg-[var(--brand-ocean-50)]"
              >
                North Miami Condo ($25k)
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SERVICE AREAS ========== */}
      <section className="py-16 bg-white" id="service-areas">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="font-heading text-3xl font-bold text-[var(--brand-slate-900)] text-center mb-12">
            Serving South Florida
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="rounded-2xl shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[var(--brand-ocean-100)] flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-[var(--brand-ocean-700)]" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-[var(--brand-slate-900)]">Miami-Dade County</h3>
                </div>
                <p className="text-sm text-[var(--brand-slate-600)] leading-relaxed mb-3">
                  Miami, Coral Gables, Kendall, Doral, Homestead, and 10+ more cities
                </p>
                <Button 
                  variant="link" 
                  onClick={() => navigate('/impact-windows/miami')}
                  className="text-[var(--brand-ocean-700)] p-0 h-auto text-sm"
                >
                  View Miami pricing →
                </Button>
              </CardContent>
            </Card>

            <Card className="rounded-2xl shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[var(--brand-ocean-100)] flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-[var(--brand-ocean-700)]" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-[var(--brand-slate-900)]">Broward County</h3>
                </div>
                <p className="text-sm text-[var(--brand-slate-600)] leading-relaxed mb-3">
                  Fort Lauderdale, Pembroke Pines, Hollywood, Plantation, and 8+ more cities
                </p>
                <Button 
                  variant="link" 
                  onClick={() => navigate('/impact-windows/fort-lauderdale')}
                  className="text-[var(--brand-ocean-700)] p-0 h-auto text-sm"
                >
                  View Fort Lauderdale pricing →
                </Button>
              </CardContent>
            </Card>

            <Card className="rounded-2xl shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[var(--brand-ocean-100)] flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-[var(--brand-ocean-700)]" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-[var(--brand-slate-900)]">Palm Beach County</h3>
                </div>
                <p className="text-sm text-[var(--brand-slate-600)] leading-relaxed mb-3">
                  Boca Raton, Delray Beach, West Palm Beach, Wellington, and 4+ more cities
                </p>
                <Button 
                  variant="link" 
                  onClick={() => navigate('/impact-windows/boca-raton')}
                  className="text-[var(--brand-ocean-700)] p-0 h-auto text-sm"
                >
                  View Boca Raton pricing →
                </Button>
              </CardContent>
            </Card>
          </div>
          <div className="text-center mt-8">
            <Button 
              variant="outline"
              onClick={() => navigate('/miami-services')}
              className="rounded-xl h-11 border-2 border-[var(--brand-ocean-600)] text-[var(--brand-ocean-700)] font-semibold hover:bg-[var(--brand-ocean-50)]"
            >
              View Full Service Area & Project Types →
            </Button>
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="py-20 bg-gradient-to-br from-[var(--brand-ocean-700)] to-[var(--brand-ocean-900)]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Get Your Quote?
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Upload photos now. Receive Good/Better/Premium pricing within 24 hours.
          </p>
          <Button 
            onClick={() => handleCTAClick('final')}
            size="lg"
            data-testid="final-cta-upload"
            className="bg-white text-[var(--brand-ocean-900)] rounded-xl h-16 px-10 text-lg font-semibold shadow-xl hover:bg-white/95 transition-all"
          >
            <Upload className="mr-2 h-5 w-5" />
            Get My Price
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <p className="text-sm text-white/70 mt-4">
            No appointment • No pressure • Fast turnaround
          </p>
        </div>
      </section>
    </div>
  );
}
